import React, { cloneElement, ReactElement, useEffect, useRef } from 'react';
import { SalvFieldHeader, SalvIconButton, SalvProgressCircle, Size } from '@salvapp/ui-components';
import { faCheck, faPenToSquare, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { highlightInput } from '../helper';
import { useFormikContext } from 'formik';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';

interface FieldViewProps {
  label: string;
  value: string;
  onEdit?: () => void;
  description?: string;
  disabled?: boolean;
  size?: Size;
}

interface FieldEditorProps<T> {
  label: string;
  field: keyof T;
  description?: string;
  disabled?: boolean;
  editedField: string | undefined;
  setEditedField: (value: string | undefined) => void;
  size?: Size;
  children: ReactElement<any>;
}

export const FieldView = ({ label, value, onEdit, description, disabled, size = Size.SMALL }: FieldViewProps) => (
  <div>
    <SalvFieldHeader
      size={size}
      label={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {label}
          {!disabled && onEdit && <SalvIconButton icon={faPenToSquare} onClick={onEdit} ariaLabel={`Edit ${label}`} />}
        </div>
      }
      className={'m-b-4'}
      description={description}
    />
    <div>{value || '-'}</div>
  </div>
);

const FieldEditor = <T,>({
  label,
  description,
  disabled = false,
  field,
  editedField,
  setEditedField,
  size = Size.SMALL,
  children: input,
}: FieldEditorProps<T>) => {
  const { values, setFieldValue, isSubmitting: isSubmitting, submitForm, resetForm, errors } = useFormikContext<T>();
  const isDropdown = Array.isArray(input.props['options']);
  const value = isDropdown
    ? input.props['options'].find(
        (option: DropdownOption) => option.value.toString() === (values[field] as string).toString()
      )!
    : (values[field] as string);

  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const isEditing = editedField === field;
  useEffect(() => {
    if (isEditing && inputRef.current) {
      highlightInput(inputRef.current);
    }
  }, [isEditing]);

  if (isDropdown && !value) {
    return;
  }

  const displayValue = isDropdown ? value.label : value;

  const onChange = (value: DropdownOption[] | string) =>
    typeof value === 'string'
      ? setFieldValue(field.toString(), value)
      : setFieldValue(field.toString(), value[0].value);

  const setEditing = (value: boolean) => {
    setEditedField(value ? field.toString() : undefined);
  };

  const onCancel = () => {
    resetForm();
    setEditing(false);
  };

  const onSubmit = async () => {
    if (!errors[field]) {
      await submitForm();
      setEditing(false);
    }
  };

  const inputWithProps = cloneElement(
    input as React.ReactElement<{
      ref?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
      ariaLabel: string;
      size: Size;
      value: string;
      onChange: (value: string) => void;
      disabled: boolean;
      isInvalid: boolean;
    }>,
    {
      ref: inputRef,
      ariaLabel: label,
      size,
      value,
      onChange,
      disabled: isSubmitting,
      isInvalid: !!errors[field],
    }
  );

  return isEditing ? (
    <div>
      <SalvFieldHeader
        size={size}
        description={description}
        label={
          <div style={{ display: 'flex', gap: '8px' }}>
            {label}
            <div style={{ display: 'flex', gap: '4px' }}>
              <SalvIconButton
                icon={faCheck}
                variant={'primary'}
                ariaLabel={`Confirm ${label} edit`}
                onClick={onSubmit}
              />
              <SalvIconButton
                icon={faXmark}
                variant={'secondary'}
                ariaLabel={`Cancel ${label} edit`}
                onClick={onCancel}
              />
              {isSubmitting && <SalvProgressCircle ariaLabel={`Updating ${label}`} size={Size.SMALL} />}
            </div>
          </div>
        }
      />
      {inputWithProps}
    </div>
  ) : (
    <FieldView
      label={label}
      description={description}
      value={displayValue}
      onEdit={() => setEditing(true)}
      disabled={disabled}
      size={size}
    />
  );
};

export default FieldEditor;
